import { useContent } from "@/content/ContentProvider";
import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Copy } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { BANDS, MAX_SCORE, bandIndex, decode, total } from "@/lib/scorecard";
import {
  AUDIT,
  CALL,
  callLink,
  decode as decodeCheck,
  summary,
} from "@/lib/check";
import { HOST } from "@/components/site/Host";
import { identify, track } from "@/lib/track";
import { PillButton } from "@/components/site/Kit";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
const empty = {
  name: "",
  email: "",
  company: "",
  stage: "",
  topics: [],
  message: "",
};
const MAX = 600;

function Field({ id, label, error, children, className = "", hint }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-[12px] font-normal opacity-50">
          {label}
        </Label>
        {hint}
      </div>
      {children}
      <p
        role="alert"
        className={`overflow-hidden text-[12px] text-red-600 transition-all duration-300 ${error ? "max-h-6 opacity-100" : "max-h-0 opacity-0"}`}
      >
        {error}
      </p>
    </div>
  );
}
export default function Contact() {
  const {
    text: cmsText,
    AUDIENCES,
    CONTACT_EMAIL,
    STATIC: siteStatic,
  } = useContent();
  const rules = {
    name: (v) => (v.trim() ? "" : cmsText("Contact.error-name")),
    email: (v) =>
      /^\S+@\S+\.\S+$/.test(v) ? "" : cmsText("Contact.error-email"),
    message: (v) =>
      v.trim().length >= 10 ? "" : cmsText("Contact.error-message"),
  };
  const TOPICS = siteStatic["Contact"].TOPICS;
  const STAGES = siteStatic["Contact"].STAGES;
  const [params] = useSearchParams();
  const topic = params.get("topic");
  const score = params.get("score");
  const scorecard = decode(params.get("sc"));
  const check = decodeCheck(params.get("c"));
  const audience = AUDIENCES.find((a) => a.slug === params.get("audience"));
  const [f, setF] = useState(() => ({
    ...empty,
    topics:
      topic && TOPICS.find((t) => t.toLowerCase() === topic.toLowerCase())
        ? [TOPICS.find((t) => t.toLowerCase() === topic.toLowerCase())]
        : [],
    message: check
      ? `${summary(check)} ${params.get("small") ? "We would like the small tool." : "We would like to book the Audit."} `
      : scorecard
        ? `Our scorecard result: ${total(scorecard)}/${MAX_SCORE} (${BANDS[bandIndex(scorecard)].short}). What we would like to discuss: `
        : score
          ? cmsText("Contact.score-prefill", {
              score,
              stage: params.get("stage") || "",
            })
          : audience?.contact || "",
  }));
  const messageInitialized = useRef(
    Boolean(score || scorecard || check || audience?.contact),
  );
  useEffect(() => {
    if (audience?.contact && !messageInitialized.current) {
      messageInitialized.current = true;
      setF((current) =>
        current.message ? current : { ...current, message: audience.contact },
      );
    }
  }, [audience]);
  // The topic list arrives with the page content, after the first render, so the
  // topic named in the link is applied once it is known.
  useEffect(() => {
    if (!topic) return;
    const match = TOPICS.find((t) => t.toLowerCase() === topic.toLowerCase());
    if (match)
      setF((current) =>
        current.topics.length ? current : { ...current, topics: [match] },
      );
  }, [topic, TOPICS]);
  const [err, setErr] = useState({});
  const [draft, setDraft] = useState(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [honey, setHoney] = useState("");
  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    if (k === "message") messageInitialized.current = true;
    setF((s) => ({
      ...s,
      [k]: v,
    }));
    if (err[k] && rules[k])
      setErr((x) => ({
        ...x,
        [k]: rules[k](v),
      }));
  };
  const blur = (k) => () =>
    rules[k] &&
    setErr((x) => ({
      ...x,
      [k]: rules[k](f[k]),
    }));
  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const er = Object.fromEntries(
      Object.keys(rules).map((k) => [k, rules[k](f[k])]),
    );
    setErr(er);
    if (Object.values(er).some(Boolean)) {
      toast.error(cmsText("Contact.error-fields"));
      return;
    }
    const body = [
      cmsText("Contact.email-name", { value: f.name }),
      cmsText("Contact.email-reply", { value: f.email }),
      cmsText("Contact.email-company", {
        value: f.company || cmsText("Contact.not-provided"),
      }),
      cmsText("Contact.email-workflow", {
        value: f.stage || cmsText("Contact.not-selected"),
      }),
      cmsText("Contact.email-topics", {
        value: f.topics.join(", ") || cmsText("Contact.not-selected"),
      }),
      "",
      f.message,
    ].join("\n");
    const fallback = {
      body,
      href: `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(cmsText("Contact.email-subject"))}&body=${encodeURIComponent(body)}`,
    };
    setSending(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: f.name,
          email: f.email,
          company: f.company,
          stage: f.stage,
          topics: f.topics,
          message: f.message,
          page: window.location.pathname + window.location.search,
          website: honey,
        }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || "");
      }
      identify(f.email, { name: f.name, firm: f.company || undefined });
      track("contact_form_submitted", { form: "contact" });
      setSent(true);
    } catch {
      setDraft(fallback);
    } finally {
      setSending(false);
    }
  };
  const copyMail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      toast.success(cmsText("Contact.email-copied"));
    } catch {
      toast.error(cmsText("Contact.copy-failed"));
    }
  };
  const fieldCls = (bad) =>
    `h-12 rounded-[14px] border-0 bg-white px-4 text-[15px] shadow-none transition-shadow focus-visible:ring-1 focus-visible:ring-black focus-visible:ring-offset-0 ${bad ? "ring-1 ring-red-500" : ""}`;
  return (
    <>
      <PageHero
        label={cmsText("Contact.Contact.contact.b37456")}
        title={cmsText("Contact.Contact.start-with-one-workflow-.909978")}
        text={cmsText(
          "Contact.Contact.tell-us-what-the-job-involves-which-tools-you.77efac",
        )}
      />
      <section className="px-5 pt-16 lg:pt-28">
        <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-12">
          <div className="space-y-2 lg:col-span-4">
            {/* Who reads what you send. */}
            <div
              className="rounded-[20px] bg-[#f7f7f7] p-5"
              data-testid="contact-host"
            >
              <div className="flex items-center gap-4">
                <span className="block h-14 w-14 shrink-0 overflow-hidden rounded-full bg-black">
                  <img
                    src={HOST.photo}
                    alt={HOST.name}
                    width="56"
                    height="56"
                    loading="lazy"
                    className="h-full w-full origin-[58%_38%] scale-[1.45] object-cover grayscale"
                  />
                </span>
                <div className="text-[15px] leading-[1.4]">
                  <p className="font-medium">{HOST.name}</p>
                  <p className="opacity-55">
                    Reads every message and answers within 24 hours.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-[14px] bg-white py-2 pl-4 pr-2">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="nm-link text-[17px] tracking-[-0.02em]"
                >
                  {CONTACT_EMAIL}
                </a>
                <button
                  type="button"
                  onClick={copyMail}
                  aria-label={cmsText("Contact.Contact.copy-email.e89e8c")}
                  data-testid="copy-email"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f7f7f7] transition-colors hover:bg-black hover:text-white"
                >
                  <Copy size={13} />
                </button>
              </div>
            </div>

            {/* Most people who come here want one of these four, not a form. */}
            <p className="px-2 pb-1 pt-5 text-[12px] uppercase tracking-[0.08em] opacity-45">
              Or go straight there
            </p>
            {[
              {
                id: "check",
                to: check ? `/scorecard?c=${params.get("c")}` : "/scorecard",
                label: "Find out if it is worth it",
                meta: "The check · 2 minutes",
              },
              {
                id: "book",
                to: check ? `/book?c=${params.get("c")}` : "/book",
                label: "Book the Audit",
                meta: `${AUDIT.price} · two weeks`,
              },
              {
                id: "call",
                href: callLink(check),
                label: "Talk first",
                meta: CALL.time,
              },
              {
                id: "client",
                to: "/client",
                label: "Already a client",
                meta: "Sign in",
              },
            ].map((door) => {
              const inner = (
                <>
                  <span className="text-[18px] tracking-[-0.02em]">
                    {door.label}
                  </span>
                  <span className="flex items-center gap-2 text-[13px] opacity-60">
                    {door.meta} <ArrowUpRight size={13} />
                  </span>
                </>
              );
              const cls =
                "flex items-center justify-between gap-4 rounded-[20px] bg-[#f7f7f7] p-5 transition-colors hover:bg-black hover:text-white";
              return door.href ? (
                <a
                  key={door.id}
                  href={door.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-${door.id}`}
                  onClick={() =>
                    track("cta_fit_call_clicked", { location: "contact_page" })
                  }
                  className={cls}
                >
                  {inner}
                </a>
              ) : (
                <Link
                  key={door.id}
                  to={door.to}
                  data-testid={`contact-${door.id}`}
                  className={cls}
                >
                  {inner}
                </Link>
              );
            })}
            <p className="flex flex-wrap gap-x-6 gap-y-2 px-2 pt-4 text-[14px]">
              <Link
                to="/pricing"
                className="nm-link"
                data-testid="contact-to-pricing"
              >
                What each price buys
              </Link>
              <Link
                to="/audit"
                className="nm-link"
                data-testid="contact-to-audit"
              >
                The Audit, day by day
              </Link>
              <Link to="/audit/sample" className="nm-link">
                Read a sample Audit
              </Link>
            </p>
            <p className="px-2 pt-4 text-[14px] leading-[1.4] opacity-50">
              {cmsText("Contact.send-side-note")}
            </p>
          </div>

          <form
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="rounded-[28px] bg-[#f7f7f7] p-6 sm:p-10 lg:col-span-7 lg:col-start-6"
          >
            {sent ? (
              <div
                data-testid="contact-sent"
                role="status"
                className="flex min-h-[420px] flex-col justify-center py-10 animate-in fade-in duration-700"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black text-white">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path className="nm-draw" d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                </span>
                <p className="nm-title mt-8 text-[44px] leading-none">
                  {cmsText("Contact.sent-title")}
                </p>
                <p className="mt-4 max-w-[420px] text-[18px] leading-[1.3] opacity-60">
                  {cmsText("Contact.sent-text")}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <PillButton to="/audit" data-testid="sent-to-audit">
                    What happens next
                  </PillButton>
                  <PillButton
                    variant="white"
                    onClick={() => {
                      setSent(false);
                      setF({ ...empty });
                      setErr({});
                    }}
                    icon={false}
                  >
                    {cmsText("Contact.send-another")}
                  </PillButton>
                </div>
              </div>
            ) : draft ? (
              <div
                data-testid="contact-success"
                className="flex min-h-[420px] flex-col justify-center py-10 animate-in fade-in duration-700"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black text-white">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path className="nm-draw" d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                </span>
                <p className="nm-title mt-8 text-[44px] leading-none">
                  {cmsText("Contact.send-failed-title")}
                </p>
                <p className="mt-4 max-w-[420px] text-[18px] leading-[1.3] opacity-60">
                  {cmsText("Contact.send-failed-text")}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <PillButton href={draft.href} data-testid="open-email">
                    {cmsText("Contact.Contact.open-email-app.bc54c2")}
                  </PillButton>
                  <PillButton
                    variant="white"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(draft.body);
                        toast.success(cmsText("Contact.draft-copied"));
                      } catch {
                        toast.error(cmsText("Contact.draft-copy-failed"));
                      }
                    }}
                    data-testid="copy-draft"
                  >
                    {cmsText("Contact.Contact.copy-draft.949942")}
                  </PillButton>
                  <PillButton
                    variant="white"
                    onClick={() => setDraft(null)}
                    icon={false}
                  >
                    {cmsText("Contact.Contact.edit-details.4173a9")}
                  </PillButton>
                </div>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  id="name"
                  label={cmsText("Contact.Contact.name.709a23")}
                  error={err.name}
                >
                  <Input
                    id="name"
                    autoComplete="name"
                    data-testid="contact-name"
                    value={f.name}
                    onChange={set("name")}
                    onBlur={blur("name")}
                    aria-invalid={!!err.name}
                    className={fieldCls(err.name)}
                  />
                </Field>
                <Field
                  id="email"
                  label={cmsText("Contact.Contact.work-email.432057")}
                  error={err.email}
                >
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    data-testid="contact-email"
                    value={f.email}
                    onChange={set("email")}
                    onBlur={blur("email")}
                    aria-invalid={!!err.email}
                    className={fieldCls(err.email)}
                  />
                </Field>
                <Field
                  id="company"
                  label={cmsText("Contact.Contact.company.7a1994")}
                >
                  <Input
                    id="company"
                    autoComplete="organization"
                    data-testid="contact-company"
                    value={f.company}
                    onChange={set("company")}
                    className={fieldCls()}
                  />
                </Field>
                <Field
                  id="stage"
                  label={cmsText(
                    "Contact.Contact.how-does-the-work-happen-today-.bef9a6",
                  )}
                >
                  <Select value={f.stage} onValueChange={set("stage")}>
                    <SelectTrigger
                      id="stage"
                      data-testid="contact-stage"
                      className={fieldCls()}
                    >
                      <SelectValue
                        placeholder={cmsText(
                          "Contact.Contact.select-one.4998e8",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[12px] font-normal opacity-50">
                    {cmsText(
                      "Contact.Contact.what-do-you-need-help-with-.602d9d",
                    )}
                  </Label>
                  <ToggleGroup
                    type="multiple"
                    value={f.topics}
                    onValueChange={set("topics")}
                    className="flex flex-wrap justify-start gap-2"
                    data-testid="contact-topics"
                  >
                    {TOPICS.map((t) => (
                      <ToggleGroupItem
                        key={t}
                        value={t}
                        data-testid={`topic-${t.toLowerCase().replace(/\s/g, "-")}`}
                        className="h-9 rounded-full bg-white px-4 text-[12px] font-medium transition-colors hover:bg-black/5 data-[state=on]:bg-black data-[state=on]:text-white"
                      >
                        {t}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>
                <Field
                  id="message"
                  label={cmsText(
                    "Contact.Contact.what-keeps-getting-stuck-.f9706c",
                  )}
                  error={err.message}
                  className="sm:col-span-2"
                  hint={
                    <span
                      className={`text-[12px] tabular-nums ${f.message.length > MAX - 40 ? "text-red-600" : "opacity-40"}`}
                    >
                      {f.message.length}/{MAX}
                    </span>
                  }
                >
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    rows={5}
                    maxLength={MAX}
                    value={f.message}
                    onChange={set("message")}
                    onBlur={blur("message")}
                    aria-invalid={!!err.message}
                    className={`rounded-[14px] border-0 bg-white p-4 text-[15px] shadow-none focus-visible:ring-1 focus-visible:ring-black focus-visible:ring-offset-0 ${err.message ? "ring-1 ring-red-500" : ""}`}
                  />
                </Field>
                <div
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
                >
                  <label>
                    Website
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honey}
                      onChange={(e) => setHoney(e.target.value)}
                    />
                  </label>
                </div>
                <div className="flex items-center gap-4 sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={sending}
                    className="group/roll nm-pill-btn inline-flex h-11 items-center gap-2 rounded-full bg-black px-6 text-[13px] font-medium text-white hover:bg-[#262626] disabled:opacity-70"
                  >
                    <span>
                      {sending
                        ? cmsText("Contact.sending")
                        : cmsText("Contact.send")}
                    </span>
                    <ArrowUpRight
                      size={13}
                      className="transition-transform duration-500 group-hover/roll:translate-x-0.5 group-hover/roll:-translate-y-0.5"
                    />
                  </button>
                  <span className="text-[12px] opacity-40">
                    {cmsText("Contact.send-note")}
                  </span>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}
